import React, { useState, useEffect } from "react";
import { X, User, Package, RefreshCw } from "lucide-react";
import deliveryAgentsApi from "../../api/delivery-agents";
import CustomDropdown from "../../components/common/CustomDropdown";

interface ChangeAssigneeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (order: any, agent: any, sequenceDetails?: any) => void;
  order: any;
}

const ChangeAssigneeDialog: React.FC<ChangeAssigneeDialogProps> = ({
  isOpen,
  onClose,
  onAssign,
  order,
}) => {
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [batchNumber, setBatchNumber] = useState(1);
  const [sequenceInBatch, setSequenceInBatch] = useState(1);
  const [batchDate, setBatchDate] = useState("");

  useEffect(() => {
    const fetchDeliveryAgents = async () => {
      try {
        const { deliveryAgents } = await deliveryAgentsApi.getDeliveryAgents({ isActive: true });
        setDeliveryAgents(deliveryAgents);
      } catch (error) {
        console.error("Error fetching delivery agents:", error);
      }
    };

    if (isOpen) {
      fetchDeliveryAgents();
      if (order) {
        setBatchNumber(order.batchNumber || 1);
        setSequenceInBatch(order.sequenceInBatch || 1);
        
        let d = order.batchDate;
        if (!d && order.deliveryDate) {
          d = new Date(order.deliveryDate).toISOString().split('T')[0];
        }
        if (!d) {
          d = new Date().toISOString().split('T')[0];
        }
        setBatchDate(d);
      }
    }
  }, [isOpen, order]);

  const handleAssign = () => {
    if (selectedAgent && order) {
      onAssign(order, selectedAgent, { batchNumber, sequenceInBatch, batchDate });
    }
  };

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Change Delivery Agent
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Package className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">
                Order Details
              </span>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Customer:</span> {order?.userId?.name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Meal:</span> {order?.mealId?.name}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Delivery Time:</span> {order?.deliveryTime}
              </p>
              {order?.deliveryAgentId && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Current Agent:</span> {order?.deliveryAgentId?.name}
                </p>
              )}
            </div>
          </div>

          {/* Batch & Sequencing Fields */}
          <div className="mb-6 p-4 bg-orange-50/50 rounded-lg border border-orange-100 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                Batch Number
              </label>
              <select
                value={batchNumber}
                onChange={(e) => setBatchNumber(parseInt(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white text-sm outline-none"
              >
                <option value={1}>Batch 1</option>
                <option value={2}>Batch 2</option>
                <option value={3}>Batch 3</option>
                <option value={4}>Batch 4</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                Sequence
              </label>
              <input
                type="number"
                min={1}
                value={sequenceInBatch}
                onChange={(e) => setSequenceInBatch(parseInt(e.target.value) || 1)}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Delivery Agent
            </label>
            <CustomDropdown
              options={deliveryAgents.map(agent => ({
                value: agent._id,
                label: `${agent.name} - ${agent.phoneNumber}`,
                ...agent
              }))}
              value={selectedAgent?._id || ""}
              onChange={(value) => {
                const agent = deliveryAgents.find(a => a._id === value);
                setSelectedAgent(agent);
              }}
              placeholder="Select a delivery agent"
            />
          </div>

          {selectedAgent && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedAgent.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedAgent.phoneNumber}
                  </p>
                  {selectedAgent.bikeNumber && (
                    <p className="text-sm text-gray-600">
                      Bike: {selectedAgent.bikeNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedAgent}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Change Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeAssigneeDialog; 